// 몹: Tripo GLB(고블린·방패 고블린·멧돼지). 종류별 InstancedMesh 1개 + 반전 헐 외곽선 1개.
// LOD: 종류마다 저폴리 판(mob_*_lod1.glb, 2~3천 삼각형, 같은 높이·발 원점·정면)을 따로 묶어, 카메라에서 멀거나
// 원본 메시 상한(품질 단계)을 넘는 몹은 저폴리로 그린다. 저폴리 GLB가 없으면 모두 원본으로 그린다.
// 방패 고블린 GLB(mob_shield_goblin)가 없으면 고블린 메시 + 절차 방패(actors/shield.ts) + 어두운 색조로 대신한다.
// 골격 클립은 로드 때 텍스처로 굽는다. 읽기 실패 때만 기존 절차 자세로 대신한다.
// GLB를 못 읽으면 캡슐 그레이박스로 대신해 게임은 계속 플레이된다. 프레임 루프 안에서 객체를 만들지 않는다.
import * as THREE from 'three/webgpu';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { attribute, float, mix, positionViewDirection, sin, smoothstep, step, texture, uniform, vec3, vertexColor } from 'three/tsl';
import { MOBS, MOB_CAPACITY, MOB_STATS } from '../shared/config';
import { MobKind, MobState, type GameView } from '../shared/types';
import { addSmoothNormals, loadActor, loadAnimatedActor, type ActorAsset } from './actors/gltf';
import { loadAttachments, REGION_LOOK, type RegionLook } from './actors/regionAttach';
import { BOAR_CLIPS, MOB_CLIPS, mobFrame, PoseBlend } from './actors/animation';
import { clipFrame, type SkinAtlas } from './actors/skinning';
import { buildMobMotion, ditherKeep, type MobShape } from './actors/mobMotion';
import { buildShield } from './actors/shield';
import { toonGradient } from './actors/toon';
import type { GameClocks } from './clock';
import { clamp01, phaseOf } from './util';
import { MOB_HIT_REACT } from '../shared/config';
import { SPECIAL } from '../shared/content/special';
import { MobSpecial, SPECIAL_RT } from '../shared/specialMonster';

const FLASH_TICKS = 4;
/** 외곽선 두께(월드 m). 카메라 14m, 720p에서 약 1.5px */
const OUTLINE_M = 0.02;
/** 종류별 렌더 키(m, 엘리트 배율 전). 모델 높이로 나눠 배율을 정한다. 영웅(약 2.6m)보다 작게(ART-BIBLE 2장) */
export const TARGET_H = [1.45, 1.6, 1.85, 1.5] as const;
const OUTLINE = new THREE.Color(0x0b0f1c);
/** 칠 색 위에 섞는 원래 텍스처 비율(0 = 매끈한 색면만, 1 = Tripo 텍스처 그대로) */
const PAINT_DETAIL = 0.3;
/**
 * 피격 플래시: 텍스처 명암을 그대로 둔 채 밝기만 올리고(색 × 배율), 가장자리(프레넬)를 흰빛으로 빛낸다.
 * 흰색으로 섞거나 몸 전체를 발광시키면 밝은 조명·블룸과 겹쳐 형체가 흰 덩어리로 날아간다(2026-10-13 근접 화면 확인).
 */
const HIT_BOOST = 0.6;
const ELITE_OUTLINE = new THREE.Color(0x5a0a16);
/** 스페셜 몬스터 외곽선: 황금 고블린 = 어두운 금, 챔피언 = 밝은 금테 */
const GOLD_OUTLINE = new THREE.Color(0x7a5410);
const CHAMP_OUTLINE = new THREE.Color(0xf2c14e);

/**
 * R2 잿불 협곡 전용 일반 적(20261018-r2-enemies). 게임 종류·행동·판정은 R1과 같고 렌더 모델만 바꾼다.
 * rest: 모델 공간 기준 키(m). 손에 든 무기·방패가 rest 경계 상자를 늘리므로 몸 키로 배율을 정한다.
 * 멧돼지는 R1 멧돼지 리그에 다시 묶어 길이를 맞췄으므로 R1 멧돼지 모델 키를 그대로 쓴다(같은 렌더 길이).
 */
export const R2_MOB_ACTORS = [
  { kind: MobKind.Goblin, name: 'mob_ember_goblin', shape: 'goblin', rest: 1.2 },
  { kind: MobKind.ShieldGoblin, name: 'mob_obsidian_shield', shape: 'goblin', rest: 1.3 },
  { kind: MobKind.Boar, name: 'mob_magma_boar', shape: 'boar', rest: 1.5017 },
] as const;

type N = any; // eslint-disable-line @typescript-eslint/no-explicit-any

/**
 * c021 지역3·4 변형 몹: R1 골격 모델 넷에 지역 조각(서리 결정·고드름·서리 갑판 / 버섯 갓·포자 주머니·독샘)을 이어 붙인 전용 묶음.
 * 종류·판정·행동은 그대로이고 렌더 모델만 바뀐다(actors/regionAttach.ts). 조각을 못 읽으면 기존 색조판으로 그린다.
 */
export const REGION_MOB_ACTORS = [
  { kind: MobKind.Goblin, name: 'mob_goblin', shape: 'goblin' },
  { kind: MobKind.ShieldGoblin, name: 'mob_shield_goblin', shape: 'goblin' },
  { kind: MobKind.Boar, name: 'mob_boar', shape: 'boar' },
  { kind: MobKind.Shaman, name: 'mob_mushroom_shaman', shape: 'shaman' },
] as const;

/**
 * 지역 조각 재질 값: 발광 배율(정점색 A × 배율 — 블룸 문턱 0.95 근처라 끝·샘만 번진다), 가장자리 반짝임 색(조각만),
 * 독샘 맥동 폭(0 = 고정), 주술사 시전 발광 색(R1 잿불 주황 대신 지역 색)
 */
export const REGION_ATTACH_LOOK: Readonly<Record<RegionLook, { readonly gain: number; readonly sparkle: readonly [number, number, number]; readonly pulse: number; readonly cast: readonly [number, number, number] }>> = {
  frost: { gain: 1.5, sparkle: [0.35, 0.75, 1.0], pulse: 0, cast: [0.35, 1.05, 1.8] },
  poison: { gain: 1.65, sparkle: [0.35, 0.7, 0.08], pulse: 0.4, cast: [0.7, 1.6, 0.12] },
  // C2 모래: 금빛 가장자리 반짝임(보석·독침은 정점색 발광으로 청록·호박), 주술사 시전은 청록
  sand: { gain: 1.55, sparkle: [0.62, 0.48, 0.12], pulse: 0, cast: [0.2, 1.45, 1.3] },
  // C2 심연: 자홍 반짝임, 약한 맥동(공허 불꽃·눈), 시전은 보라
  void: { gain: 1.7, sparkle: [0.7, 0.14, 0.62], pulse: 0.25, cast: [1.1, 0.3, 1.9] },
};

/** 한 종류의 인스턴스 묶음: 본체 + 외곽선이 같은 지오메트리·속성 버퍼를 공유한다. */
/**
 * 지역 색조(R1 모델을 다른 지역에 재사용할 때만): 몸 색 곱과 가장자리 발광. R2 잿불(기존), D3a R3 서리 묘지(청백 + 얼음 림), R4 늪 사원(이끼빛 + 독 림).
 * 값만 바꾸는 uniform이라 지역이 바뀌어도 셰이더를 다시 만들지 않는다
 */
const REGION_TINT: Readonly<Record<string, readonly [number, number, number, number, number, number]>> = {
  r1: [1, 1, 1, 0, 0, 0],
  r2: [0.92, 0.87, 0.83, 0.12, 0.035, 0.008],
  r3: [0.72, 0.84, 1.06, 0.035, 0.09, 0.17],
  r4: [0.78, 0.95, 0.66, 0.045, 0.11, 0.02],
  // C2: R5 모래(빛바랜 붕대·사암 금빛 + 청록 림), R6 심연(흑요석 남보라 + 자홍 림)
  r5: [1.08, 0.94, 0.7, 0.02, 0.12, 0.11],
  r6: [0.55, 0.48, 0.72, 0.16, 0.03, 0.22],
};

class Batch {
  readonly tint = uniform(new THREE.Color(1, 1, 1));
  readonly rim = uniform(new THREE.Color(0, 0, 0));
  /** 지역 색조 적용(같은 값이면 아무것도 하지 않는다) */
  setRegion(t: readonly number[]): void {
    const c = this.tint.value as THREE.Color, r = this.rim.value as THREE.Color;
    if (c.r !== t[0] || c.g !== t[1] || c.b !== t[2]) c.setRGB(t[0], t[1], t[2]);
    if (r.r !== t[3] || r.g !== t[4] || r.b !== t[5]) r.setRGB(t[3], t[4], t[5]);
  }
  readonly mesh: THREE.InstancedMesh;
  readonly hull: THREE.InstancedMesh;
  readonly aM = new THREE.InstancedBufferAttribute(new Float32Array(MOB_CAPACITY * 4), 4);
  readonly aN = new THREE.InstancedBufferAttribute(new Float32Array(MOB_CAPACITY * 4), 4);
  readonly aT = new THREE.InstancedBufferAttribute(new Float32Array(MOB_CAPACITY * 4), 4);
  readonly aPose = new THREE.InstancedBufferAttribute(new Float32Array(MOB_CAPACITY * 3), 3);
  count = 0;
  /** 렌더 키(m)를 맞추는 모델 배율 */
  unit = 1;
  private readonly materials: THREE.Material[] = [];

  constructor(geometry: THREE.BufferGeometry, shape: MobShape, height: number, color: (mo: ReturnType<typeof buildMobMotion>) => N, readonly skin?: SkinAtlas, readonly look?: RegionLook) {
    for (const a of [this.aM, this.aN, this.aT, this.aPose]) a.setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute('aM', this.aM);
    geometry.setAttribute('aN', this.aN);
    geometry.setAttribute('aT', this.aT);
    if (skin) geometry.setAttribute('aPose', this.aPose);

    const body = buildMobMotion(shape, height, 0, skin);
    const toon = new THREE.MeshToonNodeMaterial({ gradientMap: toonGradient() });
    toon.positionNode = body.position;
    toon.normalNode = body.normalView;
    // 지역 조각(aAtt.w ≥ 0.5): 몸 색조를 받지 않고 자기 정점색. 몸은 기존 텍스처 × 지역 색조
    const att: N = look && geometry.hasAttribute('aAtt') ? attribute('aAtt', 'vec4') : null;
    const attMask: N = att ? step(0.5, att.w) : null;
    const bodyCol: N = color(body).mul(this.tint);
    toon.colorNode = (att ? mix(bodyCol, att.rgb, attMask) : bodyCol).mul(float(1).add(body.hit.mul(HIT_BOOST)));
    const fres = float(1).sub(body.normalView.dot(positionViewDirection).clamp(0, 1)).pow(3);
    const rimBase = vec3(1.0, 0.94, 0.78).mul(0.2);
    // 엘리트: 진홍 림 오라(가장자리만, 느린 맥동 0.3Hz). 몸 색은 텍스처를 유지한다.
    // 챔피언은 진홍 대신 금빛 림, 황금 고블린은 몸 전체에 약한 금빛 자체광(블룸 문턱 아래, 점멸 없이 느린 맥동만)
    const rimElite = mix(vec3(1.0, 0.14, 0.18), vec3(1.0, 0.76, 0.22), body.champ.max(body.gold)).mul(float(0.65).add(sin(body.phase.mul(0.03)).mul(0.15)));
    // 피격: 흰 플래시(블룸 문턱을 살짝 넘기되 몸 전체가 하얗게 날아가지 않게)
    (toon as unknown as { emissiveNode: unknown }).emissiveNode = mix(rimBase, rimElite, body.elite.max(body.gold)).mul(fres)
      .add(vec3(1.0, 0.62, 0.1).mul(body.gold.mul(float(0.07).add(sin(body.phase.mul(0.05)).mul(0.02)))))
      .add(vec3(1.0, 0.97, 0.88).mul(fres.mul(1.1)).mul(body.hit));
    const rest = attribute(skin ? 'aActorRest' : 'position', 'vec3');
    const cast = shape === 'shaman' ? step(attribute('aM', 'vec4').x.sub(MobState.Windup).abs(), 0.1) : float(0);
    const cap = smoothstep(height * 0.65, height * 0.82, rest.y);
    const hands = smoothstep(height * 0.2, height * 0.33, rest.x.abs());
    const emission = toon as unknown as { emissiveNode: N };
    const lk = look ? REGION_ATTACH_LOOK[look] : null;
    const castCol = lk ? vec3(lk.cast[0], lk.cast[1], lk.cast[2]) : vec3(1.5, 0.14, 0.008);
    emission.emissiveNode = emission.emissiveNode.add(castCol.mul(cap.max(hands)).mul(cast.mul(0.8)))
      .add((this.rim as N).mul(fres));
    if (att && lk) {
      // 조각 발광: 정점색 A(결정 끝·독샘·반점) × 배율. 독은 몹마다 위상이 다른 느린 맥동(약 1.7초), 서리는 가장자리 반짝임
      const glow = att.w.sub(1).max(0).mul(attMask);
      const pulse = lk.pulse > 0 ? float(1 - lk.pulse * 0.5).add(sin(body.phase.mul(0.06).add(rest.y.mul(9))).mul(lk.pulse * 0.5)) : float(1);
      emission.emissiveNode = emission.emissiveNode.add(att.rgb.mul(glow.mul(lk.gain)).mul(pulse))
        .add(vec3(lk.sparkle[0], lk.sparkle[1], lk.sparkle[2]).mul(fres.mul(attMask).mul(0.55)));
    }
    toon.opacityNode = ditherKeep(body.fade);
    toon.alphaTest = 0.5;

    const hullMo = buildMobMotion(shape, height, OUTLINE_M, skin);
    const hullMat = new THREE.MeshBasicNodeMaterial({ side: THREE.BackSide });
    hullMat.positionNode = hullMo.position;
    const hullBase = mix(vec3(OUTLINE.r, OUTLINE.g, OUTLINE.b), vec3(ELITE_OUTLINE.r, ELITE_OUTLINE.g, ELITE_OUTLINE.b), hullMo.elite);
    hullMat.colorNode = mix(mix(hullBase, vec3(GOLD_OUTLINE.r, GOLD_OUTLINE.g, GOLD_OUTLINE.b), hullMo.gold), vec3(CHAMP_OUTLINE.r, CHAMP_OUTLINE.g, CHAMP_OUTLINE.b), hullMo.champ);
    hullMat.opacityNode = ditherKeep(hullMo.fade);
    hullMat.alphaTest = 0.5;
    this.materials.push(toon, hullMat);

    this.mesh = this.make(geometry, toon);
    this.mesh.castShadow = true;
    this.hull = this.make(geometry, hullMat);
    this.hull.instanceMatrix = this.mesh.instanceMatrix;
  }

  private make(geometry: THREE.BufferGeometry, mat: THREE.Material): THREE.InstancedMesh {
    const m = new THREE.InstancedMesh(geometry, mat, MOB_CAPACITY);
    m.frustumCulled = false;
    m.count = 0;
    // 위치는 positionNode가 aT로 만든다. 인스턴스 행렬은 단위 행렬로 둔다.
    const id = new THREE.Matrix4();
    for (let i = 0; i < MOB_CAPACITY; i++) m.setMatrixAt(i, id);
    return m;
  }

  /** 슬롯 하나를 쓴다. 반환값은 슬롯 번호. */
  write(slot: number, mode: number, prog: number, phase: number, hit: number, yaw: number, scale: number, speed: number, flags: number, x: number, y: number, z: number): void {
    const o = slot * 4;
    const m = this.aM.array as Float32Array, n = this.aN.array as Float32Array, t = this.aT.array as Float32Array;
    m[o] = mode; m[o + 1] = prog; m[o + 2] = phase; m[o + 3] = hit;
    n[o] = yaw; n[o + 1] = scale; n[o + 2] = speed; n[o + 3] = flags;
    t[o] = x; t[o + 1] = y; t[o + 2] = z; t[o + 3] = 0;
  }

  flush(outline: boolean): void {
    this.mesh.count = this.count;
    this.hull.count = outline ? this.count : 0;
    // 빈 묶음은 그리기 목록에서 뺀다(가시성 변경은 재컴파일이 없다)
    this.mesh.visible = this.count > 0;
    this.hull.visible = outline && this.count > 0;
    this.aM.needsUpdate = true; this.aN.needsUpdate = true; this.aT.needsUpdate = true;
    if (this.skin) this.aPose.needsUpdate = true;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.skin?.texture.dispose();
    for (const m of this.materials) m.dispose();
    this.mesh.dispose(); this.hull.dispose();
  }
}

/** 캡슐 그레이박스(GLB 실패 대비). 발이 원점. */
function fallbackGeometry(shape: MobShape): { geometry: THREE.BufferGeometry; height: number } {
  const tint = (g: THREE.BufferGeometry, hex: number, tx = 0, ty = 0, tz = 0): THREE.BufferGeometry => {
    g.translate(tx, ty, tz);
    const c = new THREE.Color(hex);
    const n = g.getAttribute('position').count;
    const col = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) { col[i * 3] = c.r; col[i * 3 + 1] = c.g; col[i * 3 + 2] = c.b; }
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    return g;
  };
  if (shape !== 'boar') {
    const body = new THREE.CapsuleGeometry(0.36, 0.5, 5, 10);
    const geometry = mergeGeometries([
      tint(body, 0x7fa340, 0, 0.61),
      tint(new THREE.BoxGeometry(0.14, 0.14, 0.85), 0x7a5a35, 0.45, 0.76, 0.4),
      tint(new THREE.BoxGeometry(0.1, 0.22, 0.1), 0x6a8a38, 0.34, 1.11, 0),
      tint(new THREE.BoxGeometry(0.1, 0.22, 0.1), 0x6a8a38, -0.34, 1.11, 0),
    ], false)!;
    return { geometry, height: 1.25 };
  }
  const body = new THREE.CapsuleGeometry(0.46, 0.8, 5, 10);
  body.rotateX(Math.PI / 2);
  const geometry = mergeGeometries([
    tint(body, 0x80573a, 0, 0.5, 0),
    tint(new THREE.BoxGeometry(0.85, 0.12, 0.45), 0xeae2c8, 0, 0.4, 1.0),
    tint(new THREE.BoxGeometry(0.5, 0.35, 0.5), 0x5a3a26, 0, 0.95, -0.1),
  ], false)!;
  return { geometry, height: 1.1 };
}

/** LOD 거리 히스토그램 칸(1m) 수 */
const LOD_BINS = 64;

export class MobsVisual {
  readonly group = new THREE.Group();
  /** load()가 끝나기 전에는 아무것도 그리지 않는다 */
  ready = false;
  private gob: Batch | null = null;
  private boar: Batch | null = null;
  private shaman: Batch | null = null;
  /** 방패 고블린 본체(GLB가 있으면 전용 메시, 없으면 null이고 고블린 메시를 함께 쓴다) */
  private shieldGob: Batch | null = null;
  /** GLB가 없을 때만: 고블린 메시 위에 얹는 절차 방패 */
  private shield: Batch | null = null;
  /** 저폴리 판(없으면 null → 원본으로 그린다) */
  private gobLo: Batch | null = null;
  private boarLo: Batch | null = null;
  private shieldLo: Batch | null = null;
  /** R2 전용 묶음(종류 번호로 색인, 없으면 null → R1 색조판으로 그린다) */
  private readonly r2Hi: (Batch | null)[] = [null, null, null];
  private readonly r2Lo: (Batch | null)[] = [null, null, null];
  private readonly loBatches = new Set<Batch>();
  private readonly r2Batches = new Set<Batch>();
  private shownR2 = 0;
  private batches: Batch[] = [];
  private loading: Promise<void> | null = null;
  private coreLoading: Promise<void> | null = null;
  private r2Loading: Promise<void> | null = null;
  private makeBatch: ((asset: ActorAsset | null, shape: MobShape, target: number, rest?: number, look?: RegionLook) => Batch) | null = null;
  /** c021 지역 변형 묶음(종류 번호로 색인, null이면 R1 모델 색조판) */
  private readonly lookHi: Record<RegionLook, (Batch | null)[]> = { frost: [null, null, null, null], poison: [null, null, null, null], sand: [null, null, null, null], void: [null, null, null, null] };
  private readonly lookLo: Record<RegionLook, (Batch | null)[]> = { frost: [null, null, null, null], poison: [null, null, null, null], sand: [null, null, null, null], void: [null, null, null, null] };
  private readonly lookLoading: Partial<Record<RegionLook, Promise<void>>> = {};
  private readonly lookBatches = new Set<Batch>();
  private shownLook = 0;
  /** QA 전후 비교: 켜면 지역 변형 묶음 대신 R1 모델 색조판으로 그린다(같은 페이지에서 전·후 캡처) */
  legacyLook = false;
  private readonly lastGen = new Uint16Array(MOB_CAPACITY);
  /** 슬롯이 재사용된 틱(보간을 끊는다) */
  private readonly genTick = new Int32Array(MOB_CAPACITY).fill(-100);
  /** 슬롯별 이동 속도(0..1, 완만하게 따라간다) */
  private readonly speed = new Float32Array(MOB_CAPACITY);
  /** 피격자 떨림 방향(렌더 프레임마다 뒤집는다) */
  private shakeSign = 1;
  private readonly cycle = new Float64Array(MOB_CAPACITY);
  private readonly poses = new PoseBlend(MOB_CAPACITY);
  /** 슬롯별 카메라 거리(m), LOD 문턱 계산용 히스토그램 */
  private readonly dist = new Float32Array(MOB_CAPACITY);
  private readonly bins = new Uint16Array(LOD_BINS + 1);
  private shown = 0;
  private shownLo = 0;
  /** 품질 단계: 원본 메시 거리(m)·상한, 외곽선 방식 */
  private lodNear = 26;
  private hdCap = MOB_CAPACITY;
  private outlineMode: 'all' | 'near' | 'off' = 'all';

  /** 품질 단계 반영(실행 중 바뀔 수 있다). 외곽선은 반전 헐(드로우콜·정점 2배)이라 낮은 단계에서 줄인다. */
  setDetail(lodNear: number, hdCap: number, outline: 'all' | 'near' | 'off'): void {
    this.lodNear = lodNear;
    this.hdCap = hdCap;
    this.outlineMode = outline;
  }

  /** GLB를 읽고 InstancedMesh를 만든다. 실패해도 그레이박스로 대신하며 reject하지 않는다. */
  load(): Promise<void> {
    if (!this.loading) this.loading = this.loadCore().then(() => this.loadR2());
    return this.loading;
  }

  /** R1 몹(고블린·멧돼지·방패·주술사)만 읽는다. 첫 화면(마을)에는 필요 없어 준비 뒤 뒤에서 부른다. */
  loadCore(): Promise<void> {
    if (!this.coreLoading) this.coreLoading = this.build();
    return this.coreLoading;
  }

  /** R2 잿불 전용 몹. loadCore가 끝난 뒤에 부른다. 끝나기 전에는 R1 색조판으로 그려진다. */
  loadR2(): Promise<void> {
    if (!this.r2Loading) this.r2Loading = this.coreLoading ? this.coreLoading.then(() => this.buildR2()) : Promise.resolve();
    return this.r2Loading;
  }

  private async build(): Promise<void> {
    const [gAsset, bAsset, sAsset, shAsset] = await Promise.all([
      loadAnimatedActor('mob_goblin', MOB_CLIPS), loadAnimatedActor('mob_boar', BOAR_CLIPS), loadAnimatedActor('mob_shield_goblin', MOB_CLIPS), loadAnimatedActor('mob_mushroom_shaman', MOB_CLIPS),
    ]);
    const [gLo, bLo, sLo] = await Promise.all([
      gAsset?.skin ? gAsset.lod : loadActor('mob_goblin_lod1', true),
      bAsset?.skin ? bAsset.lod : loadActor('mob_boar_lod1', true),
      sAsset?.skin ? sAsset.lod : loadActor('mob_shield_goblin_lod1', true),
    ]);
    const make = (asset: ActorAsset | null, shape: MobShape, target: number, rest?: number, look?: RegionLook): Batch => {
      const fb = asset ? null : fallbackGeometry(shape);
      if (fb) addSmoothNormals(fb.geometry);
      const geometry = asset ? asset.geometry : fb!.geometry;
      const height = asset ? asset.height : fb!.height;
      const map = asset?.map ?? null;
      const paint = asset?.paint === true;
      const tintOf = (mo: ReturnType<typeof buildMobMotion>): N => {
        // 매끈한 칠 색(aPaint) 위에 원래 텍스처를 PAINT_DETAIL만큼 섞어 세부를 남긴다
        const tex: N = map ? texture(map).rgb : vertexColor().rgb;
        const base: N = paint ? mix(attribute('aPaint', 'vec3'), tex, PAINT_DETAIL) : tex;
        const eliteTint = mix(vec3(1, 1, 1), vec3(1.06, 0.88, 0.88), mo.elite);
        // 방패 변종(대체 모델일 때만 플래그가 선다): 투구·피부를 어둡고 차갑게
        const variantTint = mix(vec3(1, 1, 1), vec3(0.72, 0.78, 0.9), mo.variant);
        const tinted = base.mul(eliteTint).mul(variantTint);
        // 황금 고블린: 원래 명암(밝기)은 남기고 색만 금으로
        const luma = tinted.dot(vec3(0.3, 0.59, 0.11));
        return mix(tinted, vec3(1.0, 0.68, 0.1).mul(luma.mul(1.0).add(0.12)), mo.gold.mul(0.9));
      };
      const b = new Batch(geometry, shape, height, tintOf, asset?.skin, look);
      b.unit = target / (rest ?? height);
      return b;
    };
    this.gob = make(gAsset, 'goblin', TARGET_H[MobKind.Goblin]);
    this.boar = make(bAsset, 'boar', TARGET_H[MobKind.Boar]);
    this.shaman = make(shAsset, 'shaman', TARGET_H[MobKind.Shaman]);
    this.batches = [this.gob, this.boar, this.shaman];
    // 저폴리 판은 원본이 GLB일 때만 쓴다(그레이박스 대체 중에는 의미가 없다)
    if (gAsset && gLo) { this.gobLo = make(gLo, 'goblin', TARGET_H[MobKind.Goblin]); this.batches.push(this.gobLo); }
    if (bAsset && bLo) { this.boarLo = make(bLo, 'boar', TARGET_H[MobKind.Boar]); this.batches.push(this.boarLo); }
    if (sAsset) {
      this.shieldGob = make(sAsset, 'goblin', TARGET_H[MobKind.ShieldGoblin]);
      this.batches.push(this.shieldGob);
      if (sLo) { this.shieldLo = make(sLo, 'goblin', TARGET_H[MobKind.ShieldGoblin]); this.batches.push(this.shieldLo); }
    } else {
      // 대체: 고블린 메시(약간 크게) + 절차 방패. 방패는 고블린 모델 공간 기준이라 같은 배율을 쓴다.
      const gh = gAsset ? gAsset.height : 1.25;
      const sh = buildShield();
      this.shield = new Batch(sh.geometry, 'goblin', gh, () => texture(sh.map).rgb);
      this.shield.unit = TARGET_H[MobKind.ShieldGoblin] / gh;
      this.batches.push(this.shield);
    }
    this.makeBatch = make;
    for (const b of [this.gobLo, this.boarLo, this.shieldLo]) if (b) this.loBatches.add(b);
    for (const b of this.batches) this.group.add(b.mesh, b.hull);
    if (new URLSearchParams(location.search).has('qa')) (window as unknown as { __DHMOBS__: MobsVisual }).__DHMOBS__ = this;
    this.ready = true;
  }

  /**
   * c021 지역 변형(r3 서리·r4 독): loadCore가 끝난 뒤 부른다. R1 골격 GLB(브라우저 캐시)에 조각을 붙여 종류별 묶음을 만든다.
   * 끝나기 전·실패 시에는 R1 모델 지역 색조판으로 그린다. ?r34legacy(QA 전후 비교)는 읽지 않는다.
   */
  loadLook(look: RegionLook): Promise<void> {
    let p = this.lookLoading[look];
    if (!p) {
      p = (this.coreLoading ?? this.loadCore()).then(() => this.buildLook(look)).catch((err: unknown) => console.warn('지역 변형 몹 준비 실패: ' + look, err));
      this.lookLoading[look] = p;
    }
    return p;
  }

  /** 지역 변형 묶음이 하나 이상 준비됐는지(QA) */
  lookReady(look: RegionLook): boolean {
    return this.lookHi[look].some(b => b !== null);
  }

  private async buildLook(look: RegionLook): Promise<void> {
    const make = this.makeBatch;
    if (!make || new URLSearchParams(location.search).has('r34legacy')) return;
    const assets = await Promise.all(REGION_MOB_ACTORS.map(async (d) => {
      const parts = await loadAttachments(d.name, look);
      if (!parts) return null;
      return loadAnimatedActor(d.name, d.shape === 'boar' ? BOAR_CLIPS : MOB_CLIPS, parts);
    }));
    REGION_MOB_ACTORS.forEach((d, i) => {
      const asset = assets[i];
      if (!asset?.skin || !asset.geometry.hasAttribute('aAtt')) { asset?.geometry.dispose(); return; }
      const target = TARGET_H[d.kind];
      const hi = make(asset, d.shape, target, undefined, look);
      this.lookHi[look][d.kind] = hi; this.lookBatches.add(hi); this.batches.push(hi);
      this.group.add(hi.mesh, hi.hull);
      if (asset.lod) {
        const lo = make(asset.lod, d.shape, target, undefined, look);
        this.lookLo[look][d.kind] = lo; this.lookBatches.add(lo); this.loBatches.add(lo); this.batches.push(lo);
        this.group.add(lo.mesh, lo.hull);
      }
    });
  }

  /** R2 전용: 골격 클립이 있을 때만 쓴다(없으면 R1 색조판 그대로). */
  private async buildR2(): Promise<void> {
    // ?r2legacy(QA 전후 비교): 전용 모델을 읽지 않고 기존 R1 색조판으로 그린다
    const legacy = new URLSearchParams(location.search).has('r2legacy');
    const make = this.makeBatch;
    if (!make) return;
    const r2 = legacy ? R2_MOB_ACTORS.map(() => null) : await Promise.all(R2_MOB_ACTORS.map(d => loadAnimatedActor(d.name, d.shape === 'boar' ? BOAR_CLIPS : MOB_CLIPS)));
    R2_MOB_ACTORS.forEach((d, i) => {
      const asset = r2[i];
      if (!asset?.skin) { asset?.geometry.dispose(); if (!legacy) console.warn('R2 전용 몹을 쓰지 못해 R1 색조판으로 그린다: ' + d.name); return; }
      const hi = make(asset, d.shape, TARGET_H[d.kind], d.rest);
      this.r2Hi[d.kind] = hi; this.r2Batches.add(hi); this.batches.push(hi);
      this.group.add(hi.mesh, hi.hull);
      if (asset.lod) {
        const lo = make(asset.lod, d.shape, TARGET_H[d.kind], d.rest);
        this.r2Lo[d.kind] = lo; this.r2Batches.add(lo); this.loBatches.add(lo); this.batches.push(lo);
        this.group.add(lo.mesh, lo.hull);
      }
    });
  }

  /** 셰이더 미리 컴파일용: 모든 메시를 인스턴스 1개로 보이게 한다. */
  prewarm(on: boolean): void {
    if (!this.ready) return;
    for (const b of this.batches) {
      b.mesh.count = on ? 1 : 0;
      b.hull.count = on ? 1 : 0;
      b.mesh.visible = on;
      b.hull.visible = on;
    }
  }

  /** 맵 전환·재도전: 슬롯 세대 기록을 초기화한다. */
  reset(): void {
    this.lastGen.fill(0);
    this.genTick.fill(-100);
    this.speed.fill(0);
    this.cycle.fill(0); this.poses.reset();
  }

  /** @param cam 카메라 위치(LOD 거리 기준). 생략하면 모두 원본 메시 */
  update(view: GameView, alpha: number, clocks: GameClocks, cam?: THREE.Vector3): void {
    if (!this.ready) return;
    const gob = this.gob!, boar = this.boar!, shieldGob = this.shieldGob, shield = this.shield;
    const gobLo = this.gobLo, boarLo = this.boarLo, shieldLo = this.shieldLo;
    const r2 = view.map.id === 'r2';
    const look = this.legacyLook ? undefined : REGION_LOOK[view.map.id];
    const lookHi = look ? this.lookHi[look] : null, lookLo = look ? this.lookLo[look] : null;
    // 지역 색조는 R1 모델을 재사용할 때만 입힌다(전용 모델은 자기 텍스처 그대로)
    const tint = REGION_TINT[view.map.id] ?? REGION_TINT.r1;
    for (const b of this.batches) { b.count = 0; b.setRegion(this.r2Batches.has(b) ? REGION_TINT.r1 : tint); }
    this.shakeSign = -this.shakeSign;
    const m = view.mobs;
    const time = clocks.worldNow;
    const rate = clocks.worldRate;
    // LOD 문턱: lodNear 안이면서 가까운 순 hdCap마리까지 원본(1m 칸 히스토그램, 할당 없음)
    let hdR = Infinity;
    if (cam && this.loBatches.size) {
      const bins = this.bins;
      bins.fill(0);
      for (let i = 0; i < m.capacity; i++) {
        if (m.state[i] === MobState.Inactive) continue;
        const d = Math.hypot(m.x[i] - cam.x, m.y[i] - cam.y, m.z[i] - cam.z);
        this.dist[i] = d;
        bins[Math.min(LOD_BINS, Math.floor(d))]++;
      }
      const near = Math.min(LOD_BINS, Math.floor(this.lodNear));
      let acc = 0, r = 0;
      while (r < near && acc + bins[r] <= this.hdCap) { acc += bins[r]; r++; }
      hdR = r;
    }
    // 계약 필드(gen)가 아직 채워지지 않은 빌드에서도 동작하도록 확인한다
    const gen = m.gen as Uint16Array | undefined;
    for (let i = 0; i < m.capacity; i++) {
      // 다운·기상은 전용 모션이 생길 때까지 경직 자세로 그린다(렌더 레인에서 교체)
      const rawState = m.state[i];
      // 땅속으로 사라지는 황금 고블린(Dying인데 hp가 남음, game/mobs.ts vanish): 쓰러지는 자세 대신 선 채로 가라앉는다
      const vanishing = rawState === MobState.Dying && m.hp[i] > 0;
      const st = vanishing ? MobState.Idle : rawState === MobState.Down || rawState === MobState.Wakeup ? MobState.Stunned : rawState;
      if (st === MobState.Inactive) continue;
      if (gen && gen[i] !== this.lastGen[i]) { this.lastGen[i] = gen[i]; this.genTick[i] = view.tick; this.speed[i] = 0; this.cycle[i] = 0; this.poses.reset(i); }
      const kind = m.kind[i];
      const stats = MOB_STATS[kind];
      const elite = m.elite[i] !== 0;
      // 슬롯이 방금 재사용됐다면 이전 위치(prev)는 다른 몹의 것이다 → 보간하지 않는다.
      const fresh = view.tick - this.genTick[i] <= 1;
      const a = fresh ? 1 : alpha;
      const px = fresh ? m.x[i] : m.prevX[i] + (m.x[i] - m.prevX[i]) * a;
      let py = fresh ? m.y[i] : m.prevY[i] + (m.y[i] - m.prevY[i]) * a;
      if (vanishing) py -= Math.min(1, clamp01(m.stateTick[i] / MOBS.dyingTicks) * 2.2) * 2.2;
      const pz = fresh ? m.z[i] : m.prevZ[i] + (m.z[i] - m.prevZ[i]) * a;
      const tick = phaseOf(m.stateTick[i], rate, alpha);

      // 이동 속도 0..1(종류별 기준 속도 대비). 각성 슬로·히트스톱 중에는 직전 값을 유지한다.
      if (rate >= 0.15 && !fresh) {
        const d = Math.hypot(m.x[i] - m.prevX[i], m.z[i] - m.prevZ[i]) * 60;
        const target = clamp01(d / (rate * stats.speed));
        this.speed[i] += (target - this.speed[i]) * 0.2;
      }

      let prog = 0;
      if (st === MobState.Spawning) prog = clamp01(tick / MOBS.spawnTicks);
      else if (st === MobState.Windup) prog = clamp01(tick / Math.max(1, m.windupTicks[i] || stats.windup));
      else if (st === MobState.Attack) prog = clamp01(tick / Math.max(1, stats.attackTicks));
      else if (st === MobState.Dying) prog = clamp01(tick / MOBS.dyingTicks);
      // 공중에서는 상태 경과 틱이 위상(공중제비 각도)이 된다. 그 외에는 월드 시간(히트스톱·슬로에 멈춤·느려짐)
      const phase = st === MobState.Airborne ? tick : time + i * 7;
      const ht = m.hitTick[i];
      const hit = ht < FLASH_TICKS && st !== MobState.Dying ? 1 - ht / FLASH_TICKS : 0;
      // 피격 반응(렌더 전용): 영웅 반대쪽으로 튕겼다 돌아오고, 히트스톱 동안은 맞은 몹만 좌우로 떤다(MOB_HIT_REACT)
      let rx = px, rz = pz;
      if (ht < MOB_HIT_REACT.recoilTicks && st !== MobState.Dying && st !== MobState.Spawning && st !== MobState.Airborne) {
        let dx = px - view.hero.x, dz = pz - view.hero.z;
        const len = Math.hypot(dx, dz);
        if (len > 1e-3) {
          dx /= len; dz /= len;
          const k = 1 - ht / MOB_HIT_REACT.recoilTicks;
          const amp = MOB_HIT_REACT.recoilM * (kind === MobKind.Boar ? MOB_HIT_REACT.boarScale : 1);
          const off = amp * k * k;
          rx += dx * off; rz += dz * off;
          if (ht === 0 && view.hitstop > 0) {
            // 피격자 떨림: 타격 방향에 수직으로, 렌더 프레임마다 번갈아
            const s = this.shakeSign * MOB_HIT_REACT.shakeM;
            rx += -dz * s; rz += dx * s;
          }
        }
      }
      const sp = m.special ? m.special[i] : 0;
      const eliteScale = sp === MobSpecial.Champion ? SPECIAL.champion.scale : sp === MobSpecial.GoldGoblin ? SPECIAL_RT.goldScale : elite ? MOBS.elite.scale : 1;
      const dedicated = r2 && kind !== MobKind.Shaman ? this.r2Hi[kind] : lookHi ? lookHi[kind] : null;
      // 방패 고블린 대체 모델: 고블린 메시에 방패 변종 플래그(색조)와 절차 방패를 얹는다
      const proxy = kind === MobKind.ShieldGoblin && shieldGob === null && !dedicated;
      const flags = (elite ? 1 : 0) + (proxy ? 2 : 0) + (sp === MobSpecial.GoldGoblin ? 4 : 0) + (sp === MobSpecial.Champion ? 8 : 0);
      const lo = this.dist[i] >= hdR;
      let batch = kind === MobKind.Shaman ? this.shaman! : kind === MobKind.Boar ? boar : kind === MobKind.ShieldGoblin && shieldGob ? shieldGob : gob;
      // 방패 대체 모델은 절차 방패와 짝이라 원본 고블린 메시를 그대로 쓴다
      if (dedicated) {
        const dLo = r2 ? this.r2Lo[kind] : lookLo ? lookLo[kind] : null;
        batch = lo && dLo ? dLo : dedicated;
      } else if (lo && !proxy && kind !== MobKind.Shaman) {
        const l = kind === MobKind.Boar ? boarLo : kind === MobKind.ShieldGoblin ? shieldLo : gobLo;
        if (l) batch = l;
      }
      const scale = (proxy ? shield!.unit : batch.unit) * eliteScale;
      if (batch.skin) {
        const skin = batch.skin;
        const moving = st === MobState.Chase || (kind === MobKind.Boar && st === MobState.Attack);
        const locomotion = kind === MobKind.Boar && st === MobState.Attack ? skin.clips.charge : skin.clips.run;
        // 누적 위상에 실제 이동량을 적분한다. 배속 변경 때 클립 시각이 튀지 않는다.
        const velocity = !fresh && rate > 0 ? Math.hypot(m.x[i] - m.prevX[i], m.z[i] - m.prevZ[i]) * 60 / rate : 0;
        const playback = moving ? velocity / Math.max(0.1, locomotion.speed * scale) * (locomotion.direction ?? 1) : 1;
        this.cycle[i] += clocks.worldDelta / 60 * playback;
        const frame = mobFrame(skin, kind, st, tick, this.cycle[i], m.windupTicks[i] || stats.windup);
        // 이족 공격만 한 클립 안의 연속 구간이다. 멧돼지는 windup→charge 사이를 섞는다.
        this.poses.update(i, st, frame, time, st === MobState.Attack && kind !== MobKind.Boar);
        let from = this.poses.a[i], to = this.poses.b[i], blend = this.poses.mix[i];
        // 슈퍼아머 멧돼지는 게임 이동을 유지한 채 짧은 피격 자세만 겹친다.
        if (kind === MobKind.Boar && m.hitTick[i] < 12 && st !== MobState.Dying && st !== MobState.Spawning) {
          from = frame; to = clipFrame(skin, 'hit', m.hitTick[i] / 60);
          blend = Math.sin(Math.PI * Math.min(1, (m.hitTick[i] + 1) / 12)) * 0.3;
        }
        batch.aPose.setXYZ(batch.count, from, to, blend);
      }
      batch.write(batch.count++, st, prog, phase, hit, m.facing[i], scale, this.speed[i], flags, rx, py, rz);
      if (proxy) shield!.write(shield!.count++, st, prog, phase, hit, m.facing[i], scale, this.speed[i], flags, rx, py, rz);
    }
    let shown = 0, shownLo = 0, shownR2 = 0, shownLook = 0;
    const om = this.outlineMode;
    for (const b of this.batches) {
      const isLo = this.loBatches.has(b);
      b.flush(om === 'all' || (om === 'near' && !isLo));
      if (b !== shield) shown += b.count;
      if (isLo) shownLo += b.count;
      if (this.r2Batches.has(b)) shownR2 += b.count;
      if (this.lookBatches.has(b)) shownLook += b.count;
    }
    this.shownLook = shownLook;
    this.shown = shown;
    this.shownLo = shownLo;
    this.shownR2 = shownR2;
  }

  get visible(): number {
    return this.shown;
  }

  /** 저폴리로 그린 몹 수(QA) */
  get visibleLod(): number {
    return this.shownLo;
  }

  /** R2 전용 모델로 그린 몹 수(QA) */
  get visibleDedicated(): number {
    return this.shownR2;
  }

  /** 지역3·4 변형 모델로 그린 몹 수(QA) */
  get visibleLook(): number {
    return this.shownLook;
  }

  /** 로드된 지역 변형 묶음 종류(QA) */
  lookKinds(look: RegionLook): number[] {
    return REGION_MOB_ACTORS.filter(d => this.lookHi[look][d.kind] !== null).map(d => d.kind);
  }

  /** 로드된 R2 전용 묶음 종류(QA) */
  get dedicatedKinds(): number[] {
    return R2_MOB_ACTORS.filter(d => this.r2Hi[d.kind] !== null).map(d => d.kind);
  }

  dispose(): void {
    for (const b of this.batches) b.dispose();
    this.batches = [];
  }
}
